import { t } from "@travily/i18n";
import { Icon } from "@ui/components/Icon/Icon";
import { ListSectionHeader } from "@ui/components/internal/ListSectionHeader";
import { searchableListBox } from "@ui/components/internal/SearchableListBox";
import { ScrollArea } from "@ui/components/ScrollArea";
import type { StandardAutocompleteSection } from "@ui/components/StandardAutocomplete";
import { tv } from "@ui/lib/tv";
import { SearchField, SearchFieldInput } from "@ui/primitives/Autocomplete";
import { ListBox, ListBoxHeader, ListBoxItem, ListBoxSection } from "@ui/primitives/ListBox";
import { OverlayTriggerStateContext } from "@ui/primitives/Popover";
import { composeRenderProps } from "@ui/primitives/shared";
import { useContext, useMemo } from "react";
import { getDefaultWhereSuggestions } from "../data/default-where-suggestions";
import type { WhereSelection, WhereSuggestion } from "../types";

const wherePopover = tv({
  slots: {
    root: "flex min-h-0 flex-col gap-3 p-5",
    title: "shrink-0 border-b border-neutral-100 pb-3 text-2xl/8 font-extrabold tracking-2xl text-neutral-900",
    // Search field + results/suggestions list. Reused on desktop (inside the
    // Where popover) and on mobile (as the Where accordion section body).
    panel: "flex min-h-0 flex-col gap-3",
    listGroup: "flex min-h-0 flex-col gap-1",
    searchGroup: [
      "flex h-10 shrink-0 items-center gap-1 rounded-[80px] bg-white px-3",
      "shadow-keyline",
      "focus-within:shadow-[0_0_0_1px_var(--color-neutral-300)]",
    ],
    searchIcon: "shrink-0 text-neutral-400 [&>svg]:size-5",
    sectionLabel: "text-sm/5 text-neutral-600",
    itemLabel: "min-w-0 flex-1 truncate text-base font-semibold tracking-[-0.16px] text-neutral-600",
    itemLabelActive: "text-neutral-900",
  },
});

// 9 rows × 40px + 8 gaps × 4px = 392px — the full default suggestion set, so the
// suggestions never scroll and only search results reach for the rail. Bleeding
// to the card edge puts the rail where the design has it; the viewport pads the
// rows back in line with the heading and search field.
const whereListClassName = "-mx-5 max-h-[392px]";
const whereViewportClassName = "px-5";
// The currently-selected suggestion row tints blue via aria-current.
const whereItemClassName = "aria-[current=true]:bg-blue-50";

export interface WherePopoverProps {
  value: WhereSelection | null;
  onChange: (value: WhereSelection | null) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchResults?: StandardAutocompleteSection[];
  isSearching?: boolean;
  suggestions?: WhereSuggestion[];
}

/**
 * Where section body: location search field plus destination results /
 * suggestion shortcuts. Shared by the desktop Where popover and the mobile
 * stacked search (Where section).
 */
export function WhereSearchPanel({
  value,
  onChange,
  searchValue,
  onSearchChange,
  searchResults,
  isSearching,
  suggestions,
}: WherePopoverProps) {
  const styles = wherePopover();
  // Search-field row, lists, rows, and empty state share the searchable-list
  // chrome; Where keeps its pill search group and entity icons.
  const listStyles = searchableListBox({ icon: "lg", tint: "subtle" });
  const resolvedSuggestions = useMemo(() => suggestions ?? getDefaultWhereSuggestions(), [suggestions]);
  const showResults = searchValue.trim().length >= 2;
  const selectedSuggestionId = value?.kind === "suggestion" ? value.id : null;
  // Read by DialogTrigger ancestor; calling .close() dismisses the popover after a
  // confirmed selection so the consumer doesn't have to wire isOpen state.
  const overlayState = useContext(OverlayTriggerStateContext);

  const handleSuggestionAction = (key: React.Key) => {
    const id = String(key);
    const suggestion = resolvedSuggestions.find((s) => s.id === id);
    if (!suggestion) return;
    onChange({ kind: "suggestion", id: suggestion.id, name: suggestion.name });
    overlayState?.close();
  };

  const handleDestinationAction = (key: React.Key) => {
    const id = String(key);
    const item = (searchResults ?? []).flatMap((s) => s.items).find((i) => i.id === id);
    if (!item) return;
    onChange({ kind: "destination", id: item.id, name: item.name, description: item.description });
    overlayState?.close();
  };

  return (
    <div className={styles.panel()}>
      <SearchField
        aria-label={t("components.search_bar.where_title")}
        className={listStyles.searchField()}
        onChange={onSearchChange}
        value={searchValue}
      >
        <div className={styles.searchGroup()}>
          <span aria-hidden="true" className={styles.searchIcon()}>
            <Icon name="search" />
          </span>
          <SearchFieldInput
            className={listStyles.searchInput()}
            placeholder={t("components.search_bar.where_placeholder")}
          />
        </div>
      </SearchField>

      {showResults ? (
        <ScrollArea
          className={whereListClassName}
          render={
            <ListBox
              aria-label={t("components.search_bar.where_results_label")}
              items={searchResults ?? []}
              onAction={handleDestinationAction}
              renderEmptyState={() => (
                <div className={listStyles.empty({ className: "py-6" })}>
                  {isSearching ? t("components.search_bar.searching") : t("components.search_bar.no_results")}
                </div>
              )}
              selectionMode="none"
            >
              {(section) => (
                <ListBoxSection id={section.id}>
                  {section.title ? (
                    <ListBoxHeader className="pt-2 pb-1">
                      <ListSectionHeader>{section.title}</ListSectionHeader>
                    </ListBoxHeader>
                  ) : null}
                  {section.items.map((item) => {
                    const label = item.description ?? item.name;
                    return (
                      <ListBoxItem
                        key={item.id}
                        className={composeRenderProps("", (className) =>
                          listStyles.item({ className: [whereItemClassName, className] }),
                        )}
                        id={item.id}
                        textValue={label}
                      >
                        <span aria-hidden="true" className={listStyles.itemIcon()}>
                          <Icon name="location" type="sticker" />
                        </span>
                        <span className={`${styles.itemLabel()} ${styles.itemLabelActive()}`}>{label}</span>
                      </ListBoxItem>
                    );
                  })}
                </ListBoxSection>
              )}
            </ListBox>
          }
          viewportClassName={listStyles.list({ className: whereViewportClassName })}
        />
      ) : (
        <div className={styles.listGroup()}>
          <p className={styles.sectionLabel()}>{t("components.search_bar.where_suggestions_label")}</p>
          <ScrollArea
            className={whereListClassName}
            render={
              <ListBox
                aria-label={t("components.search_bar.where_suggestions_label")}
                onAction={handleSuggestionAction}
                selectionMode="none"
              >
                {resolvedSuggestions.map((suggestion) => {
                  const isActive = suggestion.id === selectedSuggestionId;
                  return (
                    <ListBoxItem
                      key={suggestion.id}
                      aria-current={isActive || undefined}
                      className={composeRenderProps("", (className) =>
                        listStyles.item({ className: [whereItemClassName, className] }),
                      )}
                      id={suggestion.id}
                      textValue={suggestion.name}
                    >
                      <span aria-hidden="true" className={listStyles.itemIcon()}>
                        {suggestion.icon}
                      </span>
                      <span className={`${styles.itemLabel()}${isActive ? ` ${styles.itemLabelActive()}` : ""}`}>
                        {suggestion.name}
                      </span>
                    </ListBoxItem>
                  );
                })}
              </ListBox>
            }
            viewportClassName={listStyles.list({ className: whereViewportClassName })}
          />
        </div>
      )}
    </div>
  );
}

export function WherePopover(props: WherePopoverProps) {
  const styles = wherePopover();
  return (
    <div className={styles.root()}>
      <h2 className={styles.title()}>{t("components.search_bar.where_title")}</h2>
      <WhereSearchPanel {...props} />
    </div>
  );
}
